import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { uploadImageToCloudinary } from '../../../utils/cloudinaryUpload';
import { useFixedPopoverPosition } from './useFixedPopover';

/**
 * Editable image block. Offers both a direct file upload (sent straight to
 * Cloudinary, same as the video uploader) and pasting an existing URL.
 */
export default function EditableImage({
  blockId,
  editable = false,
  src,
  alt,
  onChange,
  className = '',
  placeholder,
  style,
}) {
  const [open, setOpen] = useState(false);
  const [draftSrc, setDraftSrc] = useState(src || '');
  const [draftAlt, setDraftAlt] = useState(alt || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);
  const fileInputRef = useRef(null);
  const pos = useFixedPopoverPosition(wrapRef, open, 'below', popoverRef);

  const commit = () => {
    onChange({ src: draftSrc.trim(), alt: draftAlt.trim() });
    setOpen(false);
  };

  // Popover is portaled to <body> (see useFixedPopoverPosition's doc comment),
  // so it's no longer a DOM descendant of wrapRef — a plain onBlur/contains
  // check would fire the moment focus leaves the trigger button and lands in
  // the portaled inputs. Use a document-level click-outside check instead.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      commit();
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftSrc, draftAlt]);

  const body = src ? (
    <img src={src} alt={alt || ''} className={className} style={style} data-block-id={blockId} />
  ) : (
    placeholder || null
  );

  if (!editable) return body;

  const openPopover = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDraftSrc(src || '');
    setDraftAlt(alt || '');
    setUploadError('');
    setOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const url = await uploadImageToCloudinary(file);
      setDraftSrc(url);
    } catch (err) {
      setUploadError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const popover = open && pos ? (
    <div
      ref={popoverRef}
      className="svcpg-popover"
      style={{ position: 'fixed', top: pos.top, left: pos.left }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {draftSrc ? (
        <img src={draftSrc} alt="" className="svcpg-popover__preview" />
      ) : null}
      <div className="svcpg-popover__field">
        <span>Upload a file</span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
          onChange={handleFileChange}
          disabled={uploading}
        />
        {uploading ? <span className="svcpg-popover__hint">Uploading…</span> : null}
        {uploadError ? <span className="svcpg-popover__error">{uploadError}</span> : null}
      </div>
      <div className="svcpg-popover__divider">or paste a URL</div>
      <label className="svcpg-popover__field">
        <span>Image URL</span>
        <input
          type="text"
          value={draftSrc}
          placeholder="https://res.cloudinary.com/…"
          onChange={(e) => setDraftSrc(e.target.value)}
        />
      </label>
      <label className="svcpg-popover__field">
        <span>Alt text</span>
        <input type="text" value={draftAlt} onChange={(e) => setDraftAlt(e.target.value)} />
      </label>
      <div className="svcpg-popover__actions">
        <button type="button" className="svcpg-toolbar__btn" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button
          type="button"
          className="svcpg-toolbar__btn svcpg-toolbar__btn--active"
          onClick={commit}
          disabled={uploading}
        >
          Apply
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="svcpg-editable svcpg-editable-image" ref={wrapRef}>
      <button type="button" className="svcpg-image-hit" onClick={openPopover} aria-label="Edit image">
        {body || <span className="svcpg-image-empty">+ Add image URL</span>}
        <span className="svcpg-pencil svcpg-pencil--image">✎</span>
      </button>

      {popover ? createPortal(popover, document.body) : null}
    </div>
  );
}
